import React, { useState, useEffect } from "react";
import { handleSubmit, loadDatasets } from "./helper.ts";
import { useNavigate } from "react-router-dom";
import {toast, ToastContainer} from 'react-toastify';import { Autocomplete, TextField, CircularProgress, Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import Button from "../../components/common/Button.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { CardBody, Col, FormGroup, Input, Row } from "reactstrap";
import { LabelText } from "../../components/common/Labeltext.tsx";
import SelectField from "../../components/common/SelectField.tsx";

interface FormData {
  predictor_name: string;
  domain: string;
  problem_type: string;
  dataset_id: number;
  is_incremental: boolean;
  shap_enabled: boolean;
  tune_enabled: boolean;
  zero_fill_enabled: boolean;
  frequency: string;
}

interface Dataset {
  id: number;
  file_name: string;
  dataset_name: string;
  created_at: string;
  modified_at: string;
  total_fields: number;
  total_records: number;
  created_by: number;
}

const problemTypeOptions: Record<string, string[]> = {
  EnergyAnomalyDetection: ["Classification"],
  CoolingSystemEfficiencyDetection: ["Classification"],
  RANSiteFailurePrediction: ["Classification"],
  Churn: ["Classification"],
  Forecast: ["Forecast"],
  Network_Quality_of_Experience_QoE_Score_Prediction: ["Regression"],
  Network_Congestion_Prediction: ["Regression"],
  Signal_Strength_Estimation: ["Regression"],
  Packet_Loss_Rate_Prediction: ["Regression"],
  Latency_Estimation: ["Regression"],
  AnomalyDetection: ["Unsupervised Learning"],
  EventVolumeForecast: ["Forecast"],
  UsageForecast: ["Forecast"]
};

const frequencyOptions = {
  "Minute": "min",
  "Hour": "hour",
  "Day": "day",
  "Week": "week",
  "Month": "month",
  "Year": "year"
};

const domainOptions = {
  "Cooling System Efficiency Detection": "CoolingSystemEfficiencyDetection",
  "RAN Site Failure Prediction": "RANSiteFailurePrediction",
  "Energy Anomaly Detection": "EnergyAnomalyDetection",
  "Anomaly Detection": "AnomalyDetection",
  "Usage Forecast": "UsageForecast",
  "Event Volume Forecast": "EventVolumeForecast",
  Forecast: "Forecast",
  Churn: "Churn",
  // "Network Quality of Experience (QoE) Score Prediction": "Network_Quality_of_Experience_QoE_Score_Prediction",
  // "Network Congestion Prediction": "Network_Congestion_Prediction",
  // "Signal Strength Estimator": "Signal_Strength_Estimation",
  // "Packet loss rate prediction": "Packet_Loss_Rate_Prediction",
  // "Latency estimation": "Latency_Estimation"
};

const domainDisplayOptions = Object.entries(domainOptions).map(
  ([label, value]) => ({ label, value })
);
const frequencyDisplayOptions = Object.keys(frequencyOptions);

const CreatePredictor = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = sessionStorage.getItem("createPredictorForm");
    return savedData
      ? JSON.parse(savedData)
      : { predictor_name: "", domain: "", problem_type: "", dataset_id: 0, is_incremental: false, shap_enabled: false, tune_enabled: false, zero_fill_enabled: false, frequency: "" };
  });

  const [isLoading, setIsLoading] = useState(false);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  //const toast = useToast();

  useEffect(() => {
    loadDatasets(setIsLoadingDatasets, setDatasets);
  }, []);

  const updateFormData = (field: keyof FormData, value: string | number | boolean) => {
    const newFormData = { ...formData, [field]: value };

    if (field === "domain") {
      newFormData.problem_type = "";
    }

    setFormData(newFormData);
  };

  const isTrainButtonEnabled = () => {
    return (
      formData.predictor_name.trim() !== "" &&
      formData.domain !== "" &&
      formData.problem_type !== "" &&
      formData.dataset_id !== 0
    );
  };
  const handleCancel = () => {
    navigate("/TrainingModal");
    sessionStorage.removeItem("createPredictorForm");
  };

  return (
    <>
      <BreadCrumb moduleName={"Training"} pageName={"Create Predictor"} />
      <ToastContainer position="top-center" />
      <div className="listingjs-table " id="customerList">
        <div className="live-preview">
          <div className="align-items-center d-flex mb-3 ">
            <h4 className="card-title mb-0 flex-grow-1">Create Predictor</h4>
          </div>
          <div className="border table-border p-3">
            <div className=''>
              <CardBody>
                <Row className="gy-3 pt-1 pb-2 ">
                  <Col lg={12}>
                    <LabelText
                      htmlFor={"Predictor Name"}
                      className={"text-danger"}
                      displayText={"Predictor Name"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"PredictorPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={"Enter your Predictor name"}
                    />
                    <Input
                      type="text"
                      value={formData.predictor_name}
                      onChange={(e) => updateFormData("predictor_name", e.target.value)}
                    />
                  </Col>
                  <Col lg={6}>
                    <LabelText
                      htmlFor={"Domain"}
                      className={"text-danger"}
                      displayText={"Domain"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"DomainPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={"Select a domain"}
                    />
                    <SelectField
                      className={""}
                      value={
                        domainDisplayOptions.find(opt => opt.value === formData.domain) || null
                      }
                      placeholder={" "}
                      handleChange={(newValue) => {
                        if (!newValue) return;
                        const domainCode = newValue.value;
                        setFormData(prev => {
                          const newFormData = { ...prev, domain: domainCode, problem_type: "" };
                          if (!problemTypeOptions[formData?.domain]?.includes("Regression") && !problemTypeOptions[formData?.domain]?.includes("Classification")) {
                            newFormData.tune_enabled = false
                          } else if (domainCode === "Forecast") {
                            newFormData.is_incremental = false;
                            newFormData.shap_enabled = false;
                          } else if (problemTypeOptions[domainCode]?.includes("Regression")) {
                            newFormData.is_incremental = false;
                          } else if (formData?.domain === "AnomalyDetection")
                            newFormData.shap_enabled = false;

                          return newFormData;
                        });
                      }}
                      options={domainDisplayOptions}
                      isRVStyle={true}
                    />
                  </Col>
                  <Col lg={formData.problem_type === "Forecast" ? 3 : 6}>
                    <LabelText
                      htmlFor={"ProblemType"}
                      className={"text-danger"}
                      displayText={"Problem Type"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"ProblemTypePlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={"Select a Problem Type"}
                    />
                    <SelectField
                      className={""}
                      value={
                        formData.problem_type
                          ? { label: formData.problem_type, value: formData.problem_type }
                          : null
                      }
                      placeholder={" "}
                      handleChange={(newValue) => {
                        if (newValue) updateFormData("problem_type", newValue.value);
                      }}
                      isDisabled={formData.domain === ""}
                      options={
                        formData.domain
                          ? problemTypeOptions[formData.domain].map(pt => ({
                            label: pt,
                            value: pt
                          }))
                          : []
                      }
                      isRVStyle={true}
                    />
                  </Col>
                  {formData.problem_type === "Forecast" && <Col lg={3}>
                    <LabelText
                      htmlFor={"frequency"}
                      className={"text-danger"}
                      displayText={"Frequency"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"frequencyPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={"Select a Frequency"}
                    />
                    <SelectField
                      className={""}
                      value={(() => {
                        const selected = Object.entries(frequencyOptions)
                          .find(([_, val]) => val === formData.frequency);
                        return selected ? { label: selected[0], value: selected[0] } : null;
                      })()}

                      placeholder={" "}
                      handleChange={(newValue) => {
                        if (newValue) updateFormData("frequency", frequencyOptions[newValue.value]);
                      }}
                      isDisabled={formData.domain === ""}
                      options={frequencyDisplayOptions.map(opt => ({
                        label: opt,
                        value: opt
                      }))
                      }
                      isRVStyle={true}
                    />
                  </Col>}
                  <Col lg={12}>
                    <LabelText
                      htmlFor={"Dataset"}
                      className={"text-danger"}
                      displayText={"Dataset Selection"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"DatasetPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={"Select a Dataset"}
                    />
                    <SelectField
                      className={""}
                      value={
                        formData.dataset_id
                          ? {
                            label:
                              datasets.find(d => d.id === formData.dataset_id)?.dataset_name || "",
                            value: formData.dataset_id
                          }
                          : null
                      }
                      placeholder={" "}
                      handleChange={(newValue) => {
                        updateFormData("dataset_id", newValue ? newValue.value : 0);
                      }}
                      options={datasets.map(ds => ({
                        label: ds.dataset_name,
                        value: ds.id
                      }))}
                      isRVStyle={true}
                      showLoadingIndicator={isLoadingDatasets}
                    />
                  </Col>
                  <Col lg={3} md={4} sm={4}>
                    <FormGroup>
                      <LabelText
                        htmlFor="shap"
                        className="text-danger"
                        displayText="SHAP"
                      />
                      <LabelText
                        htmlFor={"shapPlaceholder"}
                        classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                        displayText={"Enable SHAP"}
                      />
                      <div className="d-flex align-items-center mt-2">
                        <FormGroup switch>
                          <Input
                            type="switch"
                            className="rv-form-switch-input"
                            name="shap"
                            checked={formData.shap_enabled || formData?.domain === "AnomalyDetection"}
                            onChange={(e) => {
                              if (formData?.domain === "AnomalyDetection")
                                updateFormData("shap_enabled", true)
                              else
                                updateFormData("shap_enabled", e.target.checked)
                            }
                            }
                          />
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col lg={3} md={4} sm={4}>
                    <FormGroup>
                      <LabelText
                        htmlFor="shap"
                        className="text-danger"
                        displayText="Tune"
                      />
                      <LabelText
                        htmlFor={"shapPlaceholder"}
                        classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                        displayText={"Enable Tuning"}
                      />
                      <div className="d-flex align-items-center mt-2">
                        <FormGroup switch>
                          <Input
                            type="switch"
                            className="rv-form-switch-input"
                            name="shap"
                            checked={formData.tune_enabled}
                            onChange={(e) => {
                              setFormData(prev => {
                                const newFormData = { ...prev, tune_enabled: e.target.checked };
                                if (e.target.checked) {
                                  newFormData.is_incremental = false;
                                }
                                return newFormData;
                              });
                            }
                            }
                            disabled={!problemTypeOptions[formData?.domain]?.includes("Regression") && !problemTypeOptions[formData?.domain]?.includes("Classification")}
                          />
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col lg={3} md={4} sm={4}>
                    <FormGroup>
                      <LabelText
                        htmlFor="is_incremental"
                        className="text-danger"
                        displayText="Incremental Learning"
                      />
                      <LabelText
                        htmlFor={"incrementalPlaceholder"}
                        classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                        displayText={"Enable Incremental Learning"}
                      />
                      <div className="d-flex align-items-center mt-2">
                        <FormGroup switch>
                          <Input
                            type="switch"
                            className="rv-form-switch-input"
                            name="is_incremental"
                            checked={formData.is_incremental}
                            onChange={(e) => {
                              setFormData(prev => {
                                const newFormData = { ...prev, is_incremental: e.target.checked };
                                if (e.target.checked) {
                                  newFormData.tune_enabled = false;
                                }
                                return newFormData;
                              });
                            }}
                            disabled={formData?.domain === "Forecast" || formData?.domain === "AnomalyDetection" || problemTypeOptions[formData?.domain]?.includes("Regression")}
                          />
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </div>
          </div>
        </div>
        <Row lg={6} className=" justify-content-end  mt-4">
          <div className="live-preview">
            <div className="d-flex justify-content-end gap-2 mb-5">
              <button
                type="button"
                className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                <i className="ri-close-line label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                <span className="px-2 ms-2 me-1 fw-bold">
                  Cancel
                </span>
              </button>
              <button
                onClick={() => {
                  if (!isTrainButtonEnabled()) {
                    toast.error(
                      "Please fill all required fields before submitting."
                    );
                    return;
                  }
                  handleSubmit(setIsLoading, formData, navigate, toast);
                }}
                disabled={isLoading}
                type="submit"
                className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
              >
                <i className="ri-play-fill label-icon align-middle fs-16 me-1"></i>
                <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                  Next
                </span>
              </button>
            </div>
          </div>
        </Row>
      </div>
    </>
  );
};

export default CreatePredictor;
